// src/App.tsx
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Home, FileText, Briefcase, BarChart } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import Resumes from './pages/Resumes';
import Applications from './pages/Applications';
import Analytics from './pages/Analytics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <nav className="bg-white shadow-sm border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                  <div className="flex">
                    <div className="flex-shrink-0 flex items-center">
                      <Briefcase className="h-8 w-8 text-primary-600" />
                      <span className="ml-2 text-xl font-bold text-gray-900">
                        Career Navigator
                      </span>
                    </div>
                    <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                      <NavLink to="/" icon={<Home className="h-5 w-5" />}>
                        Dashboard
                      </NavLink>
                      <NavLink to="/resumes" icon={<FileText className="h-5 w-5" />}>
                        Resumes
                      </NavLink>
                      <NavLink to="/applications" icon={<Briefcase className="h-5 w-5" />}>
                        Applications
                      </NavLink>
                      <NavLink to="/analytics" icon={<BarChart className="h-5 w-5" />}>
                        Analytics
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/resumes" element={<Resumes />} />
                  <Route path="/applications" element={<Applications />} />
                  <Route path="/analytics" element={<Analytics />} />
                </Routes>
              </ErrorBoundary>
            </main>

            <footer className="bg-white border-t border-gray-200 mt-12">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <p className="text-center text-gray-500 text-sm">
                  Built with Cloudflare Pages + Workers AI + D1 Database
                </p>
              </div>
            </footer>
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function NavLink({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:border-primary-500 hover:text-gray-700 transition-colors"
    >
      <span className="mr-2">{icon}</span>
      {children}
    </Link>
  );
}

export default App;
